import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, ApiError } from '../api/client';
import type {
  CloudUsageResponse,
  HistoryListResponse,
  LocalActivityResponse,
  SnippetsListResponse,
} from '../api/types';
import { formatDuration, formatTimestamp, truncate } from '../lib/format';
import Icon from '../components/Icon';
import TopAppBar, { AccountActionButton } from '../components/TopAppBar';
import { useAccount } from '../auth/AccountContext';

/**
 * HomeView — Obsidian Companion dashboard.
 *
 * Aggregates four endpoints with `Promise.allSettled` so a failure in one
 * never blanks the page: cloud usage, local activity, recent history, and
 * recent snippets. Failed metrics render as `—`; a small note surfaces when
 * any fetch rejected. No data is fabricated — loading shows skeletons, an
 * entirely empty result shows an "No activity yet" state.
 */

type LoadState = 'loading' | 'ready' | 'error';

interface HomeData {
  cloud: CloudUsageResponse | null;
  local: LocalActivityResponse | null;
  history: HistoryListResponse | null;
  snippets: SnippetsListResponse | null;
  /** Short human note for the first rejection, if any. */
  errorNote: string | null;
}

const EMPTY: HomeData = {
  cloud: null,
  local: null,
  history: null,
  snippets: null,
  errorNote: null,
};

/** 12.4k / 1.2M compact formatter for large counts. */
function formatCompact(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** Trigger-badge color cycles primary → secondary → tertiary per row. Literal class strings so Tailwind JIT emits them. */
const TRIGGER_BADGE = [
  'rounded bg-primary/10 px-2 py-1 font-mono text-xs text-primary',
  'rounded bg-secondary/10 px-2 py-1 font-mono text-xs text-secondary',
  'rounded bg-tertiary/10 px-2 py-1 font-mono text-xs text-tertiary',
] as const;

/** Fetch every cloud-usage page so dashboard totals cover the full history. */
async function fetchAllCloudUsage(): Promise<CloudUsageResponse> {
  const events: CloudUsageResponse['events'] = [];
  let cursor: CloudUsageResponse['nextCursor'] = null;

  do {
    const params = new URLSearchParams();
    if (cursor) {
      params.set('cursorCompletedAt', String(cursor.completedAt));
      params.set('cursorEventId', cursor.eventId);
    }
    const path = params.size > 0 ? `/usage/cloud?${params.toString()}` : '/usage/cloud';
    const page = await apiGet<CloudUsageResponse>(path);
    events.push(...page.events);
    cursor = page.nextCursor;
  } while (cursor);

  return { events, nextCursor: null };
}

/** Fetch every local-activity page so dashboard totals cover the full history. */
async function fetchAllLocalActivity(): Promise<LocalActivityResponse> {
  const events: LocalActivityResponse['events'] = [];
  let cursor: LocalActivityResponse['nextCursor'] = null;

  do {
    const params = new URLSearchParams();
    if (cursor) {
      params.set('cursorEventTime', String(cursor.eventTime));
      params.set('cursorEventId', cursor.eventId);
    }
    const path = params.size > 0 ? `/activity/local?${params.toString()}` : '/activity/local';
    const page = await apiGet<LocalActivityResponse>(path);
    events.push(...page.events);
    cursor = page.nextCursor;
  } while (cursor);

  return { events, nextCursor: null };
}

export default function HomeView() {
  const navigate = useNavigate();
  const { user } = useAccount();
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<HomeData>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    Promise.allSettled([
      fetchAllCloudUsage(),
      fetchAllLocalActivity(),
      apiGet<HistoryListResponse>('/history?limit=5'),
      apiGet<SnippetsListResponse>('/snippets'),
    ]).then(([cloudR, localR, historyR, snippetsR]) => {
      if (cancelled) return;
      const cloud = cloudR.status === 'fulfilled' ? cloudR.value : null;
      const local = localR.status === 'fulfilled' ? localR.value : null;
      const history = historyR.status === 'fulfilled' ? historyR.value : null;
      const snippets = snippetsR.status === 'fulfilled' ? snippetsR.value : null;

      const firstErr = [cloudR, localR, historyR, snippetsR].find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      const err = firstErr?.reason;
      const errorNote =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : firstErr
              ? 'Some sections failed to load'
              : null;

      setData({ cloud, local, history, snippets, errorNote });
      setState(errorNote ? 'error' : 'ready');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Derived metrics (null when the source fetch failed).
  const totalWords = data.local
    ? data.local.events.reduce((sum, e) => sum + (e.wordCount ?? 0), 0)
    : null;
  const totalDurationMs = data.local
    ? data.local.events.reduce((sum, e) => sum + (e.durationMs ?? 0), 0)
    : null;
  const cloudEvents = data.cloud ? data.cloud.events.length : null;
  const syncStatus: boolean | null = user?.transcript_sync_enabled ?? null;

  const recentHistory = data.history?.entries.slice(0, 5) ?? [];
  const recentSnippets = data.snippets?.snippets.slice(0, 3) ?? [];

  // "No activity yet" only when every fetch succeeded but returned nothing.
  const totallyEmpty =
    state !== 'loading' &&
    data.errorNote === null &&
    (cloudEvents ?? 0) === 0 &&
    (data.local?.events.length ?? 0) === 0 &&
    recentHistory.length === 0 &&
    recentSnippets.length === 0;

  return (
    <>
      <TopAppBar
        title="Home"
        actions={<AccountActionButton />}
      />

      <div className="mx-auto max-w-max-width-content px-gutter pb-24 pt-24">
        <div className="flex flex-col items-start gap-gap-section lg:flex-row">
          {/* Left column: readiness, metrics, recent transcriptions */}
          <div className="w-full flex-1 space-y-gap-section">
            <ReadinessCard syncStatus={syncStatus} />

            <ActivityGrid
              loading={state === 'loading'}
              totalWords={totalWords}
              totalDurationMs={totalDurationMs}
              cloudEvents={cloudEvents}
              syncStatus={syncStatus}
            />

            <RecentTranscriptions
              loading={state === 'loading'}
              entries={recentHistory}
              onRowClick={() => navigate('/app/history')}
            />
          </div>

          {/* Right column: snippets */}
          <div className="w-full flex-shrink-0 space-y-gap-section lg:w-80">
            <SnippetsCard
              loading={state === 'loading'}
              snippets={recentSnippets}
              onAdd={() => navigate('/app/snippets')}
            />
          </div>
        </div>

        {totallyEmpty && (
          <div className="mt-gap-section flex flex-col items-center rounded-xl bg-surface-container-low px-6 py-8 text-center md:px-10">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-container-high text-primary">
              <Icon name="mic" />
            </span>
            <p className="mt-4 font-title-md text-title-md text-on-surface">Nothing here yet</p>
            <p className="mt-1 max-w-[48ch] font-body-sm text-body-sm text-on-surface-variant">
              Dictate with Loaf on your Android keyboard and your transcriptions and activity will populate this companion.
            </p>
            <Link
              to="/app/history"
              className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary-container px-4 py-2 font-label-lg text-label-lg text-on-primary-container transition-colors hover:bg-primary-fixed hover:text-on-primary-fixed"
            >
              Open history
              <Icon name="arrow_forward" className="text-[18px]" />
            </Link>
          </div>
        )}

        {data.errorNote && (
          <p className="mt-gap-section font-body-sm text-body-sm text-error" role="note">
            {data.errorNote}
          </p>
        )}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */

function ReadinessCard({ syncStatus }: { syncStatus: boolean | null }) {
  const detail =
    syncStatus === true
      ? 'Cloud transcript sync is enabled.'
      : syncStatus === false
        ? 'Cloud transcript sync is disabled.'
        : 'Cloud transcript sync status is unavailable.';
  const label =
    syncStatus === true
      ? 'Sync enabled'
      : syncStatus === false
        ? 'Sync disabled'
        : 'Sync unavailable';

  return (
    <div className="flex flex-col items-start gap-4 rounded-xl border border-outline-variant bg-surface-container-low p-6 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center space-x-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-container shadow-[0_0_15px_rgba(153,207,218,0.2)]">
          <Icon name="sync" fill className="text-secondary" />
        </div>
        <div>
          <h2 className="font-title-md text-title-md text-on-surface">Transcript sync</h2>
          <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">{detail}</p>
        </div>
      </div>
      <div
        className="flex min-h-11 w-full items-center justify-center space-x-2 rounded-lg border border-outline-variant bg-surface-container-high px-4 py-2 font-label-lg text-label-lg text-on-surface-variant sm:w-auto"
        role="status"
      >
        <Icon name="sync" className="text-[18px]" />
        <span>{label}</span>
      </div>
    </div>
  );
}

interface ActivityGridProps {
  loading: boolean;
  totalWords: number | null;
  totalDurationMs: number | null;
  cloudEvents: number | null;
  syncStatus: boolean | null;
}

function ActivityGrid({
  loading,
  totalWords,
  totalDurationMs,
  cloudEvents,
  syncStatus,
}: ActivityGridProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-gap-stack md:grid-cols-3">
        <div className="col-span-2 min-h-56 animate-pulse rounded-xl bg-surface-container-low p-6 md:row-span-3">
          <div className="mb-8 h-4 w-28 rounded bg-surface-container-high" />
          <div className="h-12 w-28 rounded bg-surface-container-high" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonTile key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-gap-stack md:grid-cols-3">
      <div className="col-span-2 flex min-h-56 flex-col justify-between rounded-xl bg-surface-container-high p-6 md:row-span-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-on-surface-variant">
            <Icon name="text_snippet" className="text-[20px] text-primary" />
            <span className="font-label-sm text-label-sm">Total words</span>
          </div>
          <span className="font-metadata text-metadata text-on-surface-variant">Local activity</span>
        </div>
        <div>
          <div className="font-display-lg text-display-lg tabular-nums text-on-surface">
            {totalWords === null ? '—' : formatCompact(totalWords)}
          </div>
          <p className="mt-2 max-w-[34ch] font-body-sm text-body-sm text-on-surface-variant">
            Words recorded from dictation activity on your Android keyboard.
          </p>
        </div>
      </div>

      <MetricTile
        icon="timer"
        iconClass="text-secondary"
        label="Dictation time"
        value={totalDurationMs === null ? '—' : formatDuration(totalDurationMs)}
      />
      <MetricTile
        icon="cloud_done"
        iconClass="text-tertiary"
        label="Cloud events"
        value={cloudEvents === null ? '—' : formatCompact(cloudEvents)}
      />
      <MetricTile
        icon="sync"
        iconClass="text-on-surface-variant"
        label="Sync status"
        value={syncStatus === true ? 'Synced' : syncStatus === false ? 'Off' : '—'}
      />
    </div>
  );
}

function MetricTile({
  icon,
  iconClass,
  label,
  value,
}: {
  icon: string;
  iconClass: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-h-[104px] flex-col justify-between rounded-xl bg-surface-container-low p-4 transition-colors hover:bg-surface-container-high">
      <div className="flex items-center gap-2 text-on-surface-variant">
        <Icon name={icon} className={`text-[20px] ${iconClass}`} />
        <span className="font-label-sm text-label-sm">{label}</span>
      </div>
      <div className="font-title-md text-title-md tabular-nums text-on-surface">{value}</div>
    </div>
  );
}

function SkeletonTile() {
  return (
    <div className="min-h-[104px] animate-pulse rounded-xl bg-surface-container-low p-4">
      <div className="mb-4 h-4 w-24 rounded bg-surface-container-high" />
      <div className="h-6 w-16 rounded bg-surface-container-high" />
    </div>
  );
}

interface RecentTranscriptionsProps {
  loading: boolean;
  entries: HistoryListResponse['entries'];
  onRowClick: () => void;
}

function RecentTranscriptions({ loading, entries, onRowClick }: RecentTranscriptionsProps) {
  return (
    <div className="rounded-xl border border-outline-variant bg-surface-container p-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-headline-md text-headline-md text-on-surface">Recent Transcriptions</h3>
        <Link
          to="/app/history"
          className="inline-flex min-h-11 items-center font-label-sm text-label-sm text-primary transition-colors hover:text-primary-fixed-dim"
        >
          View all <Icon name="arrow_forward" className="ml-1 text-[16px]" />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-10 w-10 animate-pulse rounded-full bg-surface-container-high" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 animate-pulse rounded bg-surface-container-high" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-surface-container-high" />
              </div>
            </div>
          ))}
        </div>
      ) : entries.length === 0 ? (
        <p className="py-8 text-center font-body-sm text-body-sm text-on-surface-variant">
          No recent transcriptions.
        </p>
      ) : (
        <div className="divide-y divide-outline-variant/30 space-y-0">
          {entries.map((entry) => (
            <button
              key={entry.entry_id}
              type="button"
              onClick={onRowClick}
              className="group -mx-4 flex cursor-pointer items-center justify-between rounded-lg px-4 py-4 transition-colors hover:bg-surface-container-high"
            >
              <div className="flex max-w-[70%] items-center space-x-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-variant text-on-surface-variant transition-colors group-hover:bg-primary-container group-hover:text-on-primary-container">
                  <Icon name="mic" className="text-[20px]" />
                </div>
                <div className="truncate">
                  <p className="truncate font-body-md text-body-md text-on-surface">
                    {truncate(entry.output_text || entry.original_text || 'Untitled', 80)}
                  </p>
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    {formatTimestamp(entry.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 font-label-sm text-label-sm text-on-surface-variant">
                <span>{formatDuration(entry.duration_ms)}</span>
                <Icon
                  name="play_arrow"
                  className="text-[16px] opacity-0 transition-opacity group-hover:opacity-100"
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface SnippetsCardProps {
  loading: boolean;
  snippets: SnippetsListResponse['snippets'];
  onAdd: () => void;
}

function SnippetsCard({ loading, snippets, onAdd }: SnippetsCardProps) {
  return (
    <div className="flex min-h-[400px] flex-col rounded-xl border border-outline-variant bg-surface-container-low p-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-headline-md text-headline-md text-on-surface">Snippets</h3>
        <Icon name="content_copy" className="text-[20px] text-on-surface-variant" />
      </div>

      <div className="flex-grow space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-lg border border-outline-variant/50 bg-surface p-3"
            >
              <div className="mb-2 h-5 w-16 rounded bg-surface-container-high" />
              <div className="h-4 w-full rounded bg-surface-container-high" />
            </div>
          ))
        ) : snippets.length === 0 ? (
          <p className="py-8 text-center font-body-sm text-body-sm text-on-surface-variant">No snippets yet.</p>
      ) : (
        snippets.map((snippet, idx) => {
          const badgeClass = TRIGGER_BADGE[idx % TRIGGER_BADGE.length];
          return (
            <button
              type="button"
              key={snippet.snippet_id}
              onClick={onAdd}
              aria-label={`Open snippet ${snippet.trigger} in Snippets`}
              className="group flex w-full cursor-pointer flex-col rounded-lg border border-outline-variant/50 bg-surface p-3 text-left transition-colors hover:border-primary/50"
            >
              <div className="mb-1 flex items-start justify-between">
                <code className={badgeClass}>{snippet.trigger}</code>
                <Icon
                  name="edit"
                  className="text-[14px] text-on-surface-variant opacity-0 transition-opacity group-hover:opacity-100"
                />
              </div>
              <p className="truncate font-body-sm text-body-sm text-on-surface">
                {truncate(snippet.expansion, 60)}
              </p>
            </button>
          );
        })
      )}
      </div>

      <button
        type="button"
        onClick={onAdd}
        className="mt-6 flex h-[52px] w-full items-center justify-center space-x-2 rounded-xl bg-primary-container font-label-lg text-label-lg text-on-primary-container transition-colors duration-200 hover:bg-primary-fixed hover:text-on-primary-fixed active:scale-95"
      >
        <Icon name="add" />
        <span>Add Snippet</span>
      </button>
    </div>
  );
}
